"use client";

import {
  animate,
  type MotionValue,
  motion,
  useInView,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
} from "framer-motion";
import { type JSX, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { SectionHeading } from "../../home/section";
import { EASE } from "../../home/use-case-tabs/constants";

export type SceneProps = {
  /** 0→1 playback position for this scene, driven by the autoplay timer. */
  progress: MotionValue<number>;
  reduce: boolean;
  /** Render at natural height with no inner scroll (mobile, stacked layout). */
  flow?: boolean;
  /** Whether this scene is the one currently on stage. Defaults to true. */
  active?: boolean;
};

const clampCount = (v: number, total: number) => Math.max(1, Math.min(total, Math.ceil(v * total)));

/**
 * Converts a scene's 0→1 scroll progress into how many thread items should be
 * revealed. Reduced motion (and the mobile full-render) shows everything.
 *
 * Inactive scenes hold the full conversation rather than tracking progress, so
 * the outgoing scene never visibly rewinds when you jump to another case.
 */
export function useRevealCount(
  progress: MotionValue<number>,
  total: number,
  reduce: boolean,
  active = true,
) {
  const [count, setCount] = useState(() => (reduce ? total : clampCount(progress.get(), total)));
  useMotionValueEvent(progress, "change", (v) => {
    if (reduce || !active) return;
    const next = clampCount(v, total);
    setCount((prev) => (prev === next ? prev : next));
  });
  // On activation, snap the reveal to the current scroll position (e.g. the
  // start of the scene after a click) instead of inheriting a stale count.
  useEffect(() => {
    if (reduce || !active) return;
    setCount(clampCount(progress.get(), total));
  }, [active, reduce, progress, total]);

  if (reduce) return total;
  return active ? count : total;
}

export type UseCase = {
  id: string;
  title: string;
  description: string;
  Scene: (props: SceneProps) => JSX.Element;
  /** Number of reveal steps in the scene; tunes the autoplay duration. */
  steps?: number;
};

// Autoplay timing. Each scene plays for `steps × PER_STEP_MS` (with a floor),
// holds fully-revealed for `HOLD_MS`, then the next case takes the stage.
const PER_STEP_MS = 900;
const MIN_PLAY_MS = 3400;
const HOLD_MS = 1700;

function SceneLayer({
  Scene,
  active,
  progress,
  reduce,
}: {
  Scene: (props: SceneProps) => JSX.Element;
  active: boolean;
  progress: MotionValue<number>;
  reduce: boolean;
}) {
  return (
    <motion.div
      className="absolute inset-0"
      initial={false}
      animate={{ opacity: active ? 1 : 0 }}
      transition={{ duration: 0.35, ease: EASE }}
      style={{ pointerEvents: active ? "auto" : "none", zIndex: active ? 1 : 0 }}
    >
      <Scene progress={progress} reduce={reduce} active={active} />
    </motion.div>
  );
}

function CaseItem({
  title,
  description,
  active,
  progress,
  onSelect,
}: {
  title: string;
  description: string;
  active: boolean;
  progress: MotionValue<number>;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className="block w-full text-left focus-visible:outline-none"
      >
        <h3
          className={cn(
            "text-xl font-bold tracking-tight transition-colors duration-300 sm:text-2xl",
            active ? "text-foreground" : "text-muted-foreground/50 hover:text-muted-foreground",
          )}
        >
          {title}
        </h3>
        <motion.div
          initial={false}
          animate={{ height: active ? "auto" : 0, opacity: active ? 1 : 0 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="overflow-hidden"
        >
          <p className="mt-2 max-w-md leading-relaxed text-muted-foreground">{description}</p>
          <div className="mt-4 h-0.5 w-full max-w-md overflow-hidden rounded-full bg-border">
            <motion.div
              className="h-full origin-left rounded-full bg-foreground"
              style={{ scaleX: active ? progress : 0 }}
            />
          </div>
        </motion.div>
      </button>
    </li>
  );
}

export function FeatureUseCases({
  eyebrow,
  title,
  cases,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  cases: UseCase[];
  className?: string;
}) {
  const reduce = useReducedMotion() ?? false;
  const stageRef = useRef<HTMLDivElement>(null);
  const inView = useInView(stageRef, { amount: 0.4 });
  const [active, setActive] = useState(0);
  // Bumped on every selection so clicking the already-active case replays it.
  const [replay, setReplay] = useState(0);
  const n = cases.length;

  // Single 0→1 playback value, shared by the active scene and its progress bar.
  const progress = useMotionValue(reduce ? 1 : 0);
  // Mobile renders the full conversation under each block (no playback).
  const full = useMotionValue(1);

  const select = useCallback((i: number) => {
    setActive(i);
    setReplay((r) => r + 1);
  }, []);

  // Autoplay engine: while the stage is on screen, play the active scene from
  // the start, hold on its finished state, then advance to the next case and
  // loop. Re-runs on selection (including replays) and when scrolled into view.
  useEffect(() => {
    if (reduce) {
      progress.set(1);
      return;
    }
    if (!inView) return;

    progress.set(0);
    const steps = cases[active]?.steps ?? 6;
    const playSeconds = Math.max(MIN_PLAY_MS, steps * PER_STEP_MS) / 1000;

    let holdTimer: ReturnType<typeof setTimeout> | undefined;
    const controls = animate(progress, 1, {
      duration: playSeconds,
      ease: "linear",
      onComplete: () => {
        holdTimer = setTimeout(() => setActive((i) => (i + 1) % n), HOLD_MS);
      },
    });

    return () => {
      controls.stop();
      if (holdTimer) clearTimeout(holdTimer);
    };
  }, [active, replay, inView, reduce, n, cases, progress]);

  const heading = (
    <SectionHeading align="left" eyebrow={eyebrow} title={title} animate={false} size="sm" />
  );

  return (
    <section className={cn("border-t border-border bg-background", className)}>
      {/* Mobile: heading + stacked cases, each scene rendered complete inline. */}
      <div className="mx-auto max-w-7xl px-4 py-20 lg:hidden">
        {heading}
        <ol className="mt-10 space-y-12">
          {cases.map((c) => {
            const Scene = c.Scene;
            return (
              <li key={c.id}>
                <h3 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                  {c.title}
                </h3>
                <p className="mt-2 max-w-md leading-relaxed text-muted-foreground">
                  {c.description}
                </p>
                <div className="mt-6">
                  <Scene progress={full} reduce flow />
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Desktop: a static two-column block. The active scene autoplays when in
          view and clicking a case restarts it. */}
      <div className="hidden lg:block">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-2 items-center gap-12 px-8 py-28 xl:gap-16 xl:py-32">
          {/* Left: heading + accordion list */}
          <div>
            {heading}
            <ol className="mt-10 space-y-4">
              {cases.map((c, i) => (
                <CaseItem
                  key={c.id}
                  title={c.title}
                  description={c.description}
                  active={i === active}
                  progress={progress}
                  onSelect={() => select(i)}
                />
              ))}
            </ol>
          </div>

          {/* Right: stage cross-fading to the active scene */}
          <div ref={stageRef} className="relative h-[480px] w-full">
            {cases.map((c, i) => (
              <SceneLayer
                key={c.id}
                Scene={c.Scene}
                active={i === active}
                progress={progress}
                reduce={reduce}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
